import { useMemo, useState } from "react";
import { TransactionType } from "../shared/Consts";
import { GICTransaction } from "../models/GICTransaction"
import { Price } from "../models/Price";
import { useDialog } from "../libs/dialog/DialogProvider";
import { IAddHoldingProps } from "../dialogs/AddHolding/IAddHolding"
import { usePath } from "../libs/path/PathProvider";
import { useTicker } from "../dialogs/AddHolding/HoldingName"
import {usePrice} from "../dialogs/AddHolding/TransPrice"
import PortAccount from "../models/PortAccount";
import PortUtils from "../portlibs/PortUtils";
import createHolding from "../models/HoldingFactory"
import {useAction} from "../dialogs/AddHolding/ActionSelect";
import dayjs from "dayjs";
import Holding from "../models/Holding";
import FormGroup from "../libs/form/FormGroup";
import Validators from "../libs/form/Validators";
import FormControl from "../libs/form/FormControl";
import Confirm from "../dialogs/Confirm";
import Redeem from "../dialogs/Redeem";
import { Transaction } from "../models/Transaction";
import createTrans from "../models/TransFactory";
import Dividend from "../dialogs/Dividend";
import Reinvest from "../dialogs/Reinvest";
import Prices from "../models/Prices";
import Portfolio from "../models/Portfolio";
import usePortfolio from "./PortfolioProvider";
import { TQueryParams } from "../libs/Url";

function getHolding(portfolio:Portfolio, holding:Holding|null, ticker:string){
    if (holding) return holding
    else {
        holding = portfolio.holding(ticker)
        return holding || createHolding(portfolio, ticker.toUpperCase(), [])
    }
}

function getAccount(transaction:Transaction|null, query:TQueryParams) {
    const account = transaction?.accountId || PortUtils.getFilter(query).account
    return account<0 ? 0 : account
}
export default function useAddHolding(props:IAddHoldingProps){
    const {transaction,holding} = props;
    const {openDialog,closeDialog} = useDialog();
    const {ticker,setTicker} = useTicker(props)
    
    const {query} = usePath();
    const [currentAccount,setCurrentAccount] = useState<number>(getAccount(transaction, query))
    
    const priceProps = usePrice(props)
    const {price,setPrice,settle} = priceProps
    const {type,setType} = useAction(props)
    
    const {portfolio} = usePortfolio()
    const isBond = Price.isBond(ticker)
    const isSave = Price.isSave(ticker)
    const isGIC = Price.isGIC(ticker)
    const isTermed = isGIC || isBond
    const isCash = Price.isCash(ticker);
    const termTrans = transaction && (isTermed||isSave) ? (transaction as GICTransaction) : null;
    const qty = transaction ? transaction.quantity : (type==TransactionType.dividend && holding && holding.price) ? holding.upcomingDividend : 0

    const [count,setCount] = useState<string>(qty.toString());
    const [note,setNote] = useState<string>(transaction?.note.toString() || "");
    const [updateCash,setUpdateCash] = useState<boolean>(false);
    const [yld,setYield] = useState<string>(termTrans?.yield.toString() || "")
    const [date,setDate] = useState<dayjs.Dayjs>(transaction ? dayjs(transaction.date) : dayjs())
    const [maturiy,setMaturity] = useState<dayjs.Dayjs>(isTermed && transaction ? dayjs( (transaction as GICTransaction).maturity ) : dayjs().add(1, "year").add(1, "day"))
    const [busy,setBusy] = useState<boolean>(false)
    const [tax,setTax] = useState<boolean>(false)
    const account = PortAccount.i

    const form = useMemo(() => new FormGroup("add", [
        new FormControl("ticker", [Validators.Required], ticker),
        new FormControl("qty", [Validators.NonZero], qty),
        new FormControl("price", [Validators.Required], price)
    ]), [])

    const isBuy = type==TransactionType.buy && parseFloat(count)>0;

    const update = async (trans:Transaction) => {
        try {
            await account.update(portfolio, trans, updateCash, parseFloat(settle)||0)
            return true;
        }catch(e:any){
            console.log(e)
            return false
        }
    }
    const onDup = async () : Promise<any> => {
        setBusy(true)
        const trans = transaction!.clone()
        trans._id = ""
        trans.date = new Date()
        if (!await update(trans)) return setBusy(false)
    }
    const onSave = async () : Promise<any> => {
        if (!form.dirty) form.markAllAsDirty()
        if (!form.valid) return

        setBusy(true)
        
        let trans;
        if (transaction) trans = transaction.clone()
        else {
            const _holding = getHolding(portfolio, holding, ticker)
            trans = createTrans(ticker, _holding, {} as any)
        }
        trans.date = date.toDate()
        trans.type = type;
        trans.accountId = currentAccount;
        trans.quantity = parseFloat(count)
        trans.price = parseFloat(price)
        trans.note = note
        if (isTermed||isSave){
            const gic = trans as GICTransaction;
            gic.maturity = isBuy ? maturiy.toDate() : date.toDate()
            gic.yield = parseFloat(yld) || 0
        }

        if (!await update(trans)) return setBusy(false)

        if (tax && !await account.tax(portfolio, trans)) return setBusy(false)
        closeDialog()
    }
    const deleteTrans = async () => {
        setBusy(true)
        try { 
            await account.deleteTrans(transaction!) 
            closeDialog()
        }catch(e){
            console.log(e)
            setBusy(false)
        }
    }
    const onRedeem = () => openDialog({id:"redeem", children:<Redeem transactions={[termTrans!]}/>})
    const onDividend = () => openDialog({id:"dividend", children:<Dividend ticker={ticker} transaction={transaction}/>})
    const onDelete = () => {
        if (!transaction) return;
        openDialog({
            id:"confirm", children:<Confirm title="Delete" message={`Are you sure you wish to delete ${transaction.holding.name}`} onYes={deleteTrans}/>
        })
    }
    const onType = (action:number) => {
        setType(action)
        if (isTermed && !isBuy) setMaturity(date)        
        switch(action){
        case TransactionType.dividend:
            setPrice("1")
            if (isTermed) setMaturity(date)
            break;
        case TransactionType.topup:
        case TransactionType.reinvest:
            break;
        default:
            const p = Prices.i.get(ticker)
            if (p) setPrice(p.price.toString())
            break;
        }
    }
    const onReinvest = () => {
        if (!transaction) return
        const account = transaction.accountId
        const holding = transaction.holding.filterByAccount(account)
        holding.transactions.length>0 && openDialog({id:"reinvest", children:<Reinvest holding={holding}/>})
    }
    
    return {
        transaction,currentAccount,setCurrentAccount,priceProps,
        ticker,setTicker,price,setPrice,type,holding,
        isSave,isGIC,isTermed,isCash,isBond,
        updateCash,setUpdateCash,
        count,setCount,note,setNote,yld,setYield,date,setDate,maturiy,setMaturity,busy,
        form, onDup, onType, onDelete, onRedeem, onSave, onReinvest, onDividend, tax, setTax
    }
}
